import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { AppRoutes } from './components/routes/AppRoutes';
import { SessionExpiredDialog } from './components/auth/SessionExpiredDialog';
import { GlobalErrorBoundary } from './components/boundaries';
import {
  AutoBackendOfflineBanner,
  AutoGlobalErrorDialog,
  ErrorStoreIntegration,
  AutoCriticalErrorPage
} from './components/error';
import { MsalOrgProvider } from './contexts/MsalContext';
import { DeletionWarningBanner } from './components/layout/DeletionWarningBanner';
import { SnackbarProvider } from './contexts/SnackbarContext';

// Hooks and utilities
import { useAuth } from './hooks/auth/useAuth';
import { useErrorHandler } from './stores/errorStore';
import { theme } from './themes/AppTheme';
import { useCancelDeletion } from './hooks/api/useUsers';
import AuthService from './services/AuthService';


function App() {
  const { t } = useTranslation();
  const errorHandler = useErrorHandler();
  const {
    authenticated,
    checking,
    sessionExpiredOpen,
    handleLogin,
    handleLogout,
    handleSessionExpiredClose,
  } = useAuth();

  const cancelDeletion = useCancelDeletion();
  const [currentUser, setCurrentUser] = useState(AuthService.getUser());

  // Update user state when authentication changes
  useEffect(() => {
    if (authenticated) {
      setCurrentUser(AuthService.getUser());
    } else {
      setCurrentUser(null);
    }
  }, [authenticated]);

  // Listen for user updates
  useEffect(() => {
    const handleUserUpdate = () => {
      setCurrentUser(AuthService.getUser());
    };

    window.addEventListener('authStateChanged', handleUserUpdate);
    return () => {
      window.removeEventListener('authStateChanged', handleUserUpdate);
    };
  }, []);

  const handleCancelDeletion = async () => {
    if (!currentUser?.username) return;

    try {
      await cancelDeletion.mutateAsync(currentUser.username);
      // Refresh user data
      setCurrentUser(AuthService.getUser());
    } catch (error) {
      console.error('Error canceling deletion:', error);
    }
  };

  if (checking) {
    return <div>{t('app.loading')}</div>;
  }

  return (
    <GlobalErrorBoundary>
      <MsalOrgProvider>
        <SnackbarProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />

            {/* Critical Error State - Overrides entire UI when backend is completely down */}
            {errorHandler.hasCriticalError ? (
              <AutoCriticalErrorPage />
            ) : (
              <>
                {/* Normal App UI */}

                {/* Backend status banner - shows at top when offline */}
                <AutoBackendOfflineBanner showRetryProgress />

                {/* Deletion warning banner - shows when user has pending deletion */}
                {authenticated && (
                  <DeletionWarningBanner
                    user={currentUser}
                    onCancelDeletion={handleCancelDeletion}
                    loading={cancelDeletion.isPending}
                  />
                )}

                <SessionExpiredDialog
                  open={sessionExpiredOpen}
                  onClose={handleSessionExpiredClose}
                />

                <AppRoutes
                  authenticated={authenticated}
                  onLogin={handleLogin}
                  onLogout={handleLogout}
                />

                {/* Global error dialog for critical errors */}
                <AutoGlobalErrorDialog showRetryActions showDismissAll />

                {/* Error store integration - dispatches errors to unified notification system */}
                <ErrorStoreIntegration />
              </>
            )}

          </ThemeProvider>
        </SnackbarProvider>
      </MsalOrgProvider>
    </GlobalErrorBoundary>
  );
}

export default App;
